import * as pc from 'playcanvas';
import { MockApiClient } from './api/mockClient';
import { GameAudio } from './audio';
import { createGameApp } from './game/app';
import { buildWorld } from './game/world';
import { spawnPedestrians } from './game/npcs';
import { Player } from './game/player';
import { BuildingLabels } from './ui/labels';
import { Hud, createTouchControls } from './ui/hud';
import { LessonOverlay } from './ui/lessonOverlay';
import { MapScreen } from './ui/mapScreen';
import { Minimap } from './ui/minimap';
import { setLocale, t } from './i18n';
import type { InteractTarget } from './game/world';

const XP_PER_COURSE = 50;
const INTERACT_KEY = pc.KEY_E;
const MAP_KEY = pc.KEY_M;
const VIEW_KEY = pc.KEY_V;

async function boot(): Promise<void> {
  setLocale('ar');
  const loadingStatus = document.getElementById('loading-status')!;
  loadingStatus.textContent = t('loading_world');
  document.getElementById('loading-tip')!.textContent = t('loading_tip');
  const bootStart = performance.now();

  // Data source: mock JSON today, World_Api in Phase 1 (Blueprint §5.2)
  const api = new MockApiClient();
  const [worldMap, profile, neighbors] = await Promise.all([
    api.getWorldMap(),
    api.getXpProfile(),
    api.getNeighbors(),
  ]);

  const canvas = document.getElementById('app-canvas') as HTMLCanvasElement;
  const { app, keyboard, camera } = createGameApp(canvas);

  const world = buildWorld(app, worldMap, profile, neighbors);
  const touch = createTouchControls();
  const player = new Player(app, keyboard, camera, world.obstacles, touch);
  spawnPedestrians(app, app.root, worldMap, () => {
    const pos = player.position;
    return { x: pos.x, z: pos.z };
  });

  const hud = new Hud();
  hud.setProfile(profile);
  const lessonOverlay = new LessonOverlay();
  const mapScreen = new MapScreen(worldMap);
  const audio = new GameAudio();
  const labels = new BuildingLabels(worldMap, camera, canvas);
  const minimap = new Minimap(worldMap, () => openTravelMap());
  hud.onLevelUp = () => audio.levelUp();

  // --- Objective banner: a light state machine that walks a new player
  // through the core loop (lesson → house upgrade → explore). ---
  const objectiveEl = document.getElementById('objective-chip')!;
  const OBJECTIVE_KEYS = ['obj_lesson', 'obj_house', 'obj_explore', 'obj_done'] as const;
  let objectiveStage = 0;
  const setObjectiveStage = (stage: number) => {
    if (stage <= objectiveStage && objectiveEl.textContent) return;
    objectiveStage = stage;
    objectiveEl.textContent = t(OBJECTIVE_KEYS[stage]);
    audio.uiTick();
    if (stage === OBJECTIVE_KEYS.length - 1) {
      window.setTimeout(() => {
        objectiveEl.style.opacity = '0';
      }, 6000);
    }
  };
  objectiveEl.textContent = t(OBJECTIVE_KEYS[0]);

  // Footstep surface: hard on roads/plazas, soft on grass.
  const onHardSurface = (x: number, z: number): boolean => {
    for (const d of worldMap.districts) {
      const half = (d.radius * (d.id === 'center' ? 0.9 : 0.55)) / 2;
      if (Math.abs(x - d.center.x) < half && Math.abs(z - d.center.z) < half) return true;
    }
    for (const r of worldMap.roads) {
      const abx = r.to.x - r.from.x;
      const abz = r.to.z - r.from.z;
      const tt = pc.math.clamp(((x - r.from.x) * abx + (z - r.from.z) * abz) / (abx * abx + abz * abz), 0, 1);
      const dist = Math.hypot(x - (r.from.x + abx * tt), z - (r.from.z + abz * tt));
      if (dist < r.width / 2 + 1.8) return true;
    }
    return false;
  };

  // --- Interaction wiring ---
  let nearTarget: InteractTarget | null = null;

  const interact = () => {
    if (!nearTarget || lessonOverlay.isOpen) return;
    const building = nearTarget.building;
    if (building.interaction === 'classroom' && building.courseId) {
      lessonOverlay.open(building.courseId, building.nameAr, (courseId) => {
        void api.completeCourse(courseId).then((updated) => {
          hud.setProfile(updated);
          hud.showXpToast(XP_PER_COURSE);
          audio.chime();
          world.rebuildMyHouse(updated); // the magic loop: lesson → XP → house upgrade
          setObjectiveStage(1);
        });
      });
    }
  };

  const openTravelMap = () => {
    if (mapScreen.isOpen || lessonOverlay.isOpen) return;
    audio.uiTick();
    mapScreen.open('travel', (x, z) => player.entity.setPosition(x, 0, z));
  };

  keyboard.on(pc.EVENT_KEYDOWN, (e: pc.KeyboardEvent) => {
    if (e.key === INTERACT_KEY) {
      interact();
    } else if (e.key === MAP_KEY) {
      openTravelMap();
    } else if (e.key === VIEW_KEY) {
      player.toggleMode();
    }
  });
  hud.onInteractPressed = interact;
  hud.onMapPressed = openTravelMap;
  hud.onViewPressed = () => player.toggleMode();
  hud.onZoomPressed = (steps) => player.zoom(steps);
  hud.onMutePressed = () => audio.toggleMuted();
  audio.onMutedChanged = (muted) => hud.setMuted(muted);
  hud.setMuted(audio.isMuted);
  hud.setViewMode(player.getMode());
  player.onModeChanged = (mode) => {
    hud.setViewMode(mode);
    audio.uiTick();
  };

  // --- Frame update ---
  app.on('update', (dt: number) => {
    if (!lessonOverlay.isOpen && !mapScreen.isOpen) {
      player.update(dt);
    }
    hud.tickFps(dt);
    labels.update();

    const pos = player.position;
    audio.tick(dt, player.isMoving, player.isSprinting, onHardSurface(pos.x, pos.z));
    minimap.update(pos.x, pos.z, player.heading);
    const district = world.districtAt(pos.x, pos.z);
    hud.setDistrict(district ? district.nameAr : '—');
    if (district) {
      mapScreen.markVisited(district.id);
      if (objectiveStage === 1 && district.id === 'residential') {
        setObjectiveStage(2);
      }
      if (objectiveStage === 2 && mapScreen.visitedCount >= worldMap.districts.length) {
        setObjectiveStage(3);
      }
    }

    nearTarget = null;
    for (const target of world.interactables) {
      const dx = pos.x - target.position.x;
      const dz = pos.z - target.position.z;
      if (Math.hypot(dx, dz) <= target.radius) {
        nearTarget = target;
        break;
      }
    }
    if (nearTarget) {
      const label =
        nearTarget.building.type === 'npc' ? t('talk_to_eva') : t('enter_lesson');
      hud.showPrompt(label);
      labels.setHighlight(nearTarget.building.id);
    } else {
      hud.hidePrompt();
      labels.setHighlight(null);
    }
  });

  app.start();

  // PoC measurement/debug harness (used by automated smoke tests)
  (window as unknown as Record<string, unknown>).__goc = {
    teleport: (x: number, z: number) => player.entity.setPosition(x, 0, z),
    getPosition: () => {
      const pos = player.position;
      return { x: pos.x, z: pos.z };
    },
    setView: (mode: 'first' | 'third' | 'heli') => player.setMode(mode),
    look: (yaw: number, pitch: number) => player.setLook(yaw, pitch),
  };

  // Hide loading screen; log the PoC cold-start metric (Blueprint §8 gate)
  const loadingScreen = document.getElementById('loading-screen')!;
  loadingStatus.textContent = t('ready');
  const bootMs = Math.round(performance.now() - bootStart);
  console.info(`[GOC] world interactive in ${bootMs} ms`);
  setTimeout(() => {
    loadingScreen.style.opacity = '0';
    setTimeout(() => loadingScreen.remove(), 450);
  }, 250);

  // Map-first entry (Blueprint DR-02): pick a drop point before the first walk.
  mapScreen.open('initial', (x, z) => {
    player.entity.setPosition(x, 0, z);
  });
}

boot().catch((err) => {
  const status = document.getElementById('loading-status');
  if (status) {
    status.textContent = String(err);
  }
  console.error('[GOC] boot failed:', err);
});
